import { t } from "../i18n/index.ts";
import {
  COVER_MAX_UPLOAD_BYTES,
  COVER_MAX_ZOOM,
  COVER_WEBP_QUALITY,
  coverFileProblem,
  coverScale,
  cropOutput,
  panCrop,
  startCrop,
  zoomCrop,
  type CoverCrop,
} from "../../shared/quest-cover.ts";
import { button, el } from "./dom.ts";

/** A picked file that can't become a cover; `message` is ready to show. */
export class CoverPickError extends Error {}

const KEY_PAN_PX = 12;
const WHEEL_ZOOM = 0.0018;

/**
 * Pick → crop → encode. Opens the 16:9 crop step over the page; resolves with the
 * encoded cover (WebP, or JPEG where the browser can't encode WebP), or null on cancel.
 */
export async function cropCover(file: File): Promise<Blob | null> {
  const problem = coverFileProblem(file);
  if (problem === "type") throw new CoverPickError(t("cover.err.type"));
  if (problem === "size") throw new CoverPickError(t("cover.err.size"));
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    try {
      await img.decode();
    } catch {
      throw new CoverPickError(t("cover.err.read"));
    }
    if (!img.naturalWidth || !img.naturalHeight) throw new CoverPickError(t("cover.err.read"));
    const crop = await cropDialog(img);
    return crop ? await encode(img, crop) : null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function cropDialog(img: HTMLImageElement): Promise<CoverCrop | null> {
  return new Promise((resolve) => {
    const overlay = el("div", "cover-crop");
    const box = el("div", "cover-crop-box");
    box.setAttribute("role", "dialog");
    box.setAttribute("aria-modal", "true");
    box.setAttribute("aria-label", t("cover.crop.title"));
    const frame = el("div", "cover-crop-frame");
    frame.tabIndex = 0;
    const photo = img.cloneNode() as HTMLImageElement;
    photo.className = "cover-crop-img";
    photo.alt = "";
    photo.draggable = false;
    frame.append(photo);
    const zoomRow = el("label", "cover-crop-zoom");
    const zoom = el("input");
    zoom.type = "range";
    zoom.min = "1";
    zoom.max = String(COVER_MAX_ZOOM);
    zoom.step = "0.01";
    zoom.value = "1";
    zoomRow.append(el("span", "", t("cover.crop.zoom")), zoom);
    const foot = el("div", "cover-crop-foot");
    const cancel = button(t("cover.crop.cancel"), "rt-back", () => close(null));
    const use = button(t("cover.crop.use"), "rt-next", () => close(crop));
    foot.append(cancel, use);
    box.append(el("h2", "rt-q", t("cover.crop.title")), el("p", "rt-sub", t("cover.crop.hint")), frame, zoomRow, foot);
    overlay.append(box);
    document.body.append(overlay);

    let crop = startCrop(frame.clientWidth || 1, frame.clientHeight || 1, img.naturalWidth, img.naturalHeight);
    const paint = () => {
      const s = coverScale(crop);
      photo.style.width = `${img.naturalWidth}px`;
      photo.style.height = `${img.naturalHeight}px`;
      photo.style.transform = `translate(${crop.x}px, ${crop.y}px) scale(${s})`;
      zoom.value = String(crop.zoom);
    };
    paint();

    const pointers = new Map<number, { x: number; y: number }>();
    let pinch: { dist: number; zoom: number } | null = null;
    const local = (e: { clientX: number; clientY: number }) => {
      const r = frame.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const spread = () => {
      const [a, b] = [...pointers.values()];
      return a && b ? { dist: Math.hypot(a.x - b.x, a.y - b.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } } : null;
    };
    frame.addEventListener("pointerdown", (e) => {
      frame.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, local(e));
      const s = spread();
      pinch = s ? { dist: s.dist, zoom: crop.zoom } : null;
      frame.classList.add("is-dragging");
    });
    frame.addEventListener("pointermove", (e) => {
      const prev = pointers.get(e.pointerId);
      if (!prev) return;
      const at = local(e);
      pointers.set(e.pointerId, at);
      const s = spread();
      if (pinch && s && pinch.dist > 0) {
        crop = zoomCrop(crop, (pinch.zoom * s.dist) / pinch.dist, s.mid.x, s.mid.y);
      } else if (pointers.size === 1) {
        crop = panCrop(crop, at.x - prev.x, at.y - prev.y);
      }
      paint();
    });
    const lift = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      const s = spread();
      pinch = s ? { dist: s.dist, zoom: crop.zoom } : null;
      if (!pointers.size) frame.classList.remove("is-dragging");
    };
    frame.addEventListener("pointerup", lift);
    frame.addEventListener("pointercancel", lift);
    frame.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        const at = local(e);
        crop = zoomCrop(crop, crop.zoom * Math.exp(-e.deltaY * WHEEL_ZOOM), at.x, at.y);
        paint();
      },
      { passive: false },
    );
    zoom.addEventListener("input", () => {
      crop = zoomCrop(crop, Number(zoom.value));
      paint();
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close(null);
        return;
      }
      if (e.key === "Enter" && e.target !== cancel) {
        e.preventDefault();
        close(crop);
        return;
      }
      if (e.target !== frame) return;
      const pan: Record<string, [number, number]> = {
        ArrowLeft: [KEY_PAN_PX, 0],
        ArrowRight: [-KEY_PAN_PX, 0],
        ArrowUp: [0, KEY_PAN_PX],
        ArrowDown: [0, -KEY_PAN_PX],
      };
      const d = pan[e.key];
      if (d) crop = panCrop(crop, d[0], d[1]);
      else if (e.key === "+" || e.key === "=") crop = zoomCrop(crop, crop.zoom * 1.1);
      else if (e.key === "-") crop = zoomCrop(crop, crop.zoom / 1.1);
      else return;
      e.preventDefault();
      paint();
    };
    window.addEventListener("keydown", onKey, true);
    const onResize = () => {
      const w = frame.clientWidth;
      const h = frame.clientHeight;
      if (!w || !h || w === crop.frameW) return;
      const r = w / crop.frameW;
      crop = panCrop({ ...crop, frameW: w, frameH: h, x: crop.x * r, y: crop.y * r }, 0, 0);
      paint();
    };
    const observer = new ResizeObserver(onResize);
    observer.observe(frame);
    requestAnimationFrame(() => {
      onResize();
      frame.focus();
    });

    function close(result: CoverCrop | null): void {
      window.removeEventListener("keydown", onKey, true);
      observer.disconnect();
      overlay.remove();
      resolve(result);
    }
  });
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

async function encode(img: HTMLImageElement, crop: CoverCrop): Promise<Blob> {
  const out = cropOutput(crop);
  const canvas = el("canvas");
  canvas.width = out.width;
  canvas.height = out.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new CoverPickError(t("cover.err.read"));
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, out.sx, out.sy, out.sw, out.sh, 0, 0, out.width, out.height);
  let blob = await toBlob(canvas, "image/webp", COVER_WEBP_QUALITY);
  // Safari can't encode WebP and hands back a PNG.
  if (!blob || blob.type !== "image/webp") blob = await toBlob(canvas, "image/jpeg", COVER_WEBP_QUALITY);
  if (blob && blob.size > COVER_MAX_UPLOAD_BYTES) blob = await toBlob(canvas, "image/jpeg", 0.7);
  if (!blob) throw new CoverPickError(t("cover.err.read"));
  return blob;
}
